import { useEffect, useState } from "react";

import { i18n } from "@lingui/core";

export type ReportSubscriptionsProps = { className?: string };
import {
  addReportSubscription,
  deleteReportSubscription,
  getOrganizationReports,
  getOrganizationUserReportSubscriptions,
} from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import { REPORT_SUBSCRIPTION_INTERVAL } from "@/constants";
import { EVENT } from "@/tracking_events";
import { ReportListItem } from "@/types/Report";
import { ReportSubscription } from "@/types/ReportSubscription";
import { Checkbox } from "@components/Field";
import Table, { TableProps } from "@components/Table";

import styles from "./ReportSubscriptions.module.css";

const REPORT_INTERVALS = {
  [REPORT_SUBSCRIPTION_INTERVAL.NONE]: L.REPORTS_INTERVAL_NEVER,
  [REPORT_SUBSCRIPTION_INTERVAL.DAILY]: L.REPORTS_INTERVAL_DAILY,
  [REPORT_SUBSCRIPTION_INTERVAL.WEEKLY]: L.REPORTS_INTERVAL_WEEKLY,
  [REPORT_SUBSCRIPTION_INTERVAL.MONTHLY]: L.REPORTS_INTERVAL_MONTHLY,
  [REPORT_SUBSCRIPTION_INTERVAL.QUARTERLY]: L.REPORTS_INTERVAL_QUARTERLY,
  [REPORT_SUBSCRIPTION_INTERVAL.YEARLY]: L.REPORTS_INTERVAL_YEARLY,
};

const ReceiveReport: React.FC<{
  report: ReportListItem;
  userId: number;
  organizationUuid: string;
  subscription?: ReportSubscription;
}> = ({
  report,
  subscription: passedSubscription,
  organizationUuid,
  userId,
}) => {
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState<
    ReportSubscription | undefined
  >(passedSubscription);

  useEffect(() => {
    setSubscription(passedSubscription);
  }, [passedSubscription]);

  const commonParams = {
    organization_uuid: organizationUuid,
    user_id: userId,
    report_uuid: report.uuid,
  };

  return (
    <Checkbox
      data-testid="user-add-step-3-report-sub-checkbox"
      className={styles.checkbox}
      label={report.title}
      disabled={saving}
      checked={subscription !== undefined}
      onChange={(e) => {
        const active = e.target.checked;

        setSaving(true);
        if (active) {
          addReportSubscription(commonParams).then((res) => {
            const data = res.data as ReportSubscription;
            postTrackingData(EVENT.ADD_REPORT_SUBSCRIPTION);
            setSaving(false);
            setSubscription(data);
          });
        } else {
          if (subscription?.id) {
            deleteReportSubscription({
              ...commonParams,
              sub_id: subscription.id,
            }).then(() => {
              postTrackingData(EVENT.REMOVE_REPORT_SUBSCRIPTION);
              setSaving(false);
              setSubscription(undefined);
            });
          }
        }
      }}
    />
  );
};

/**
 * ReportSubscriptions
 * accepts a User and a OrganizationUuid and allows reusable setting of the
 * report subscriptions for that user.
 */
const ReportSubscriptions: React.FunctionComponent<
  ReportSubscriptionsProps &
    TableProps & {
      userId: number;
      organizationUuid: string;
    }
> = ({ organizationUuid, userId, ...props }) => {
  const [reportSubscriptions, setReportSubscriptions] = useState<
    ReportSubscription[]
  >([]);

  const [organizationReports, setOrganizationReports] = useState<
    ReportListItem[]
  >([]);

  // get all the reports for the organization
  useEffect(() => {
    getOrganizationReports().then(
      ({ data }) => data && setOrganizationReports(data as ReportListItem[])
    );
  }, []);

  useEffect(() => {
    getOrganizationUserReportSubscriptions({
      organization_uuid: organizationUuid,
      user_id: userId,
    })
      .then(({ data }) => {
        if (data)
          setReportSubscriptions(
            (data as { reports: ReportSubscription[] }).reports
          );
      })
      .catch(() => {
        setReportSubscriptions([]);
      });
  }, [userId, organizationUuid]);

  return (
    <Table {...props}>
      <thead className={styles.sticky}>
        <tr>
          <th className={styles.tableName}>
            {L.ORGANIZATION_USER_REPORT_TABLE_TITLE}
          </th>
          <th className={styles.frequency}>
            {L.ORGANIZATION_INVITE_USER_SUBSCRIBE}
          </th>
        </tr>
      </thead>
      <tbody>
        {organizationReports.map((report) => (
          <tr key={report.uuid} data-testid="row">
            <td>
              <ReceiveReport
                userId={userId}
                organizationUuid={organizationUuid}
                subscription={reportSubscriptions.find(
                  ({ uuid }) => uuid === report.uuid
                )}
                report={report}
              />
            </td>
            <td>{i18n._(REPORT_INTERVALS[report.interval])}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default ReportSubscriptions;
